import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronUp, X, Award, ArrowRight, FileDown, Camera, ArrowDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { saveBlob, blobErrorToJson } from "@/lib/exportDownload";
import { vocative } from "@/lib/vocative";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { Button } from "@/components/ui/button";
import { ask } from "@/lib/confirm";
import { PAINS, SIZES, STEPS, guideOrder, currentStep, stepRoute, certRows, fmtMin, totalMin, isBase, pickField, placeLabel, QR_PATH, QR_SIZE, parseGuideText,
  roleOrder, guideFull, guideLabel, hasGuide, stepOf, ROLE_NAMES } from "@/lib/ownerGuide";

// 242: průvodce majitele. Otázka „Co tě teď nejvíc pálí?" → plán → kroky na skutečných
// stránkách (obláček + zvýrazněné tlačítko) → panel po každém kroku → ohňostroj a certifikát.
// Hotové kroky pozná server z dat, průvodce se jen ptá (každé 4 s, když běží). 243: počítá se jen to,
// co vzniklo od startu průvodce; ve formulářích vede pole po poli (štítek nahoře); konec a certifikát
// podle prototypu (ohňostroj, krémová karta) + PDF. 308: i pro ostatní role — jen kroky jejich pozice,
// místo otázek krátké uvítání, certifikát s jejich rolí.

const visible = (n) => { const r = n.getBoundingClientRect(); return r.width > 0 && r.height > 0; };
// 262: řádek uvnitř karty (např. Plátce DPH v Nastavení) — karta s overflow:hidden by vnější rámeček
// ořízla a další řádek ho překryl. Takovému prvku se rámeček kreslí dovnitř (třída pp-tour-spot--in).
const clippedRing = (n) => {
  const pos = getComputedStyle(n).position;
  if (pos !== "static" && pos !== "relative") return false;
  const b = n.getBoundingClientRect();
  for (let p = n.parentElement, i = 0; p && i < 6; p = p.parentElement, i++) {
    const o = getComputedStyle(p);
    if (o.overflowX === "visible" && o.overflowY === "visible") continue;
    const a = p.getBoundingClientRect();
    return b.left - a.left < 16 || a.right - b.right < 16 || b.top - a.top < 16 || a.bottom - b.bottom < 16;
  }
  return false;
};
const firstName = (n) => String(n || "").split(" ")[0];
const greet = (n) => vocative(firstName(n));
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

function Ring({ n, of, size = 46 }) {
  const r = 18, c = 2 * Math.PI * r;
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }} aria-hidden="true">
      <svg viewBox="0 0 44 44" width={size} height={size}>
        <circle cx="22" cy="22" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="4" />
        <circle cx="22" cy="22" r={r} fill="none" stroke="hsl(var(--accent-orange))" strokeWidth="4" strokeLinecap="round"
          strokeDasharray={`${(c * n) / Math.max(of, 1)} ${c}`} transform="rotate(-90 22 22)" />
      </svg>
      <b className="absolute inset-0 grid place-items-center text-[12px] font-extrabold tabular-nums">{n}/{of}</b>
    </span>
  );
}

function Qr() {
  return (
    <svg viewBox={`-2 -2 ${QR_SIZE + 4} ${QR_SIZE + 4}`} className="w-[132px] h-[132px] rounded-[10px] bg-white shrink-0" shapeRendering="crispEdges" role="img" aria-label="QR kód na app.proyekty.cz">
      <path d={QR_PATH} fill="#111" />
    </svg>
  );
}

// 248: přehled průvodce jako panel u zvonečku — na počítači vedle levé lišty dole, na telefonu shora
function SidePanel({ children, mobile, onClose, testid }) {
  if (mobile) {
    return (
      <>
        <div className="fixed inset-0 z-[55] bg-black/40 motion-safe:animate-in motion-safe:fade-in" onClick={onClose} aria-hidden="true" />
        <div role="dialog" aria-label="Průvodce" data-testid={testid}
          className="fixed inset-x-0 top-0 z-[56] max-h-[88dvh] overflow-y-auto bg-card text-foreground rounded-b-[28px] shadow-2xl pt-[calc(env(safe-area-inset-top)+16px)] px-5 pb-5 motion-safe:animate-in motion-safe:slide-in-from-top duration-300">
          {children}
        </div>
      </>
    );
  }
  const left = (document.querySelector("aside")?.getBoundingClientRect().right || 256) + 18;
  return (
    <>
      <div className="fixed inset-0 z-[55]" onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-label="Průvodce" data-testid={testid} style={{ left }}
        className="fixed z-[56] bottom-4 w-[400px] max-w-[calc(100vw-32px)] max-h-[min(660px,calc(100dvh-32px))] overflow-y-auto bg-card text-foreground border border-border rounded-[20px] shadow-2xl p-5 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-left-2">
        {children}
      </div>
    </>
  );
}

function Overlay({ children, testid, dark }) {
  return (
    <div className={`fixed inset-0 z-[55] grid place-items-center p-4 ${dark ? "bg-black/70" : "bg-black/50"} motion-safe:animate-in motion-safe:fade-in`} data-testid={testid}>
      <div className="w-full max-w-[560px] max-h-[92dvh] overflow-y-auto bg-card border border-border rounded-[24px] shadow-2xl p-5 sm:p-7 motion-safe:animate-in motion-safe:zoom-in-95">
        {children}
      </div>
    </div>
  );
}

// Ohňostroj z prototypu: raketa vyletí, záblesk, jiskry se rozletí a padají (3,2 s dokola).
const PAIRS = [["#ffd27a", "#ff9a4d"], ["#7ef0c0", "#ffffff"], ["#8fb6ff", "#ffffff"], ["#ff8a5c", "#ffd27a"], ["#c9a2ff", "#ffe9a8"], ["#ffd27a", "#ffffff"]];
const BURSTS = [[24, 26, 0, 150], [74, 20, 0.55, 170], [50, 12, 1.15, 130], [16, 52, 1.7, 120], [84, 46, 2.2, 130], [40, 34, 2.65, 150]];
function Fireworks() {
  const h = typeof window !== "undefined" ? window.innerHeight : 900;
  const sparks = useMemo(() => BURSTS.flatMap(([x, y, d, size], k) => Array.from({ length: 34 }, (_, j) => {
    const ang = (j / 34) * Math.PI * 2 + ((j * 7) % 5) * 0.03;
    const r = size * (j % 3 === 0 ? 0.55 : j % 3 === 1 ? 0.85 : 1) * (0.92 + ((j * 13) % 9) / 60);
    return { key: `${k}-${j}`, x, y, d, dx: Math.round(Math.cos(ang) * r), dy: Math.round(Math.sin(ang) * r), c: PAIRS[k][j % 2] };
  })), []);
  return (
    <div className="pp-fws" aria-hidden="true">
      {BURSTS.map(([x, y, d], k) => (
        <span key={k}>
          <i className="pp-rk" style={{ left: `${x}%`, top: `${y}%`, "--h": `${Math.round((h * (100 - y)) / 100)}px`, animationDelay: `${d}s` }} />
          <i className="pp-fl" style={{ left: `${x}%`, top: `${y}%`, background: `radial-gradient(circle, ${PAIRS[k][0]}, transparent 70%)`, animationDelay: `${d}s` }} />
        </span>
      ))}
      {sparks.map((p) => (
        <i key={p.key} className="pp-sp" style={{ left: `${p.x}%`, top: `${p.y}%`, "--dx": `${p.dx}px`, "--dy": `${p.dy}px`, animationDelay: `${p.d}s` }}>
          <b style={{ background: p.c, boxShadow: `0 0 6px 1px ${p.c}`, animationDelay: `${p.d}s` }} />
        </i>
      ))}
    </div>
  );
}

function Dark({ children, testid, center }) {
  return (
    <div className={`fixed inset-0 z-[55] bg-[#0e0e10] text-[#f4f4f1] overflow-y-auto ${center ? "flex flex-col items-center justify-center text-center px-6 py-10" : "grid place-items-center p-4"}`} data-testid={testid}>
      <Fireworks />
      {children}
    </div>
  );
}

// 264: obláček / nápověda — tučný nadpis a pod ním odrážky (uživatel: „jeden odstavec je matoucí“)
function GuideText({ text, counter, prefix, muted }) {
  const { title, items } = parseGuideText(text);
  const dim = muted ? "text-muted-foreground" : "text-white/60";
  return (
    <span className="block" data-testid="guide-text">
      {(counter || title || prefix) && (
        <span className="flex items-baseline gap-2">
          {counter && <span className={`${dim} tabular-nums text-[12px] font-semibold shrink-0`}>{counter}</span>}
          {(title || prefix) && <b className={`font-extrabold text-[14.5px] leading-snug ${muted ? "text-foreground" : ""}`}>{prefix}{title}</b>}
        </span>
      )}
      {items.length > 1 ? (
        <ul className={`list-disc pl-4 space-y-1 font-medium ${title || counter ? "mt-1.5" : ""} ${muted ? "marker:text-muted-foreground" : "marker:text-white/50"}`}>
          {items.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      ) : items[0] ? <span className={`block font-medium ${title || counter ? "mt-1" : ""}`}>{items[0]}</span> : null}
    </span>
  );
}

// 397: obláček u prvku celý na obrazovce — nad prvkem, když se tam vejde, jinak pod ním; u velkého prvku
// (kalendář přes celou výšku) uvnitř u jeho horního okraje bez šipky. Prvek mimo obrazovku → u okraje se šipkou v textu.
export function placePill(a, h, w, vw, vh, menu) {
  const M = 12, G = 10;
  let left = Math.max(M, Math.min(a.l, vw - w - M));
  const arrow = Math.min(Math.max(a.l - left + 18, 14), w - 26);
  if (menu) {   // 263: otevřené menu (Přidat záznam) — obláček vedle něj, ne přes položky
    if (menu.left - w - M >= M) return { left: menu.left - w - M, top: menu.top };
    return { left: Math.max(M, Math.min(menu.left, vw - w - M)), top: menu.bottom + G };
  }
  if (a.t > vh - 48) return { left, top: vh - 24 - h, off: "down" };
  if (a.b < 48) return { left, top: 24, off: "up" };
  if (a.t - G - h >= M) return { left, top: a.t - G - h, side: "above", arrow };
  if (a.b + G + h <= vh - M) return { left, top: a.b + G, side: "below", arrow };
  return { left, top: Math.min(Math.max(a.t + G, M), vh - h - M) };
}

function PillBox({ anchor, w, menuEl, steps, children }) {
  const ref = useRef(null);
  const [h, setH] = useState(0);
  useLayoutEffect(() => {   // výška až po vykreslení; jiný text (Další) obláček zvětší → ResizeObserver
    const el = ref.current;
    if (!el) return undefined;
    const upd = () => setH(el.offsetHeight);
    upd();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const p = placePill(anchor, h || 120, w, window.innerWidth, window.innerHeight, menuEl?.getBoundingClientRect());
  return (
    <div ref={ref} className={`fixed z-[60] w-max rounded-[12px] bg-[#17181b]/80 backdrop-blur-xl backdrop-saturate-150 border border-white/10 shadow-[0_14px_36px_rgba(0,0,0,0.28)] text-white px-3 py-2 text-[13.5px] font-semibold leading-snug ${steps ? "pointer-events-auto" : "pointer-events-none"}`}
      style={{ left: p.left, top: p.top, maxWidth: w }} data-testid="guide-pill" data-side={p.side || p.off || "inside"}
      onPointerDown={steps ? (e) => e.stopPropagation() : undefined}>
      {children(p.off)}
      {p.side && (
        <span aria-hidden="true" className="absolute w-0 h-0 border-[7px] border-transparent"
          style={p.side === "below" ? { left: p.arrow, bottom: "100%", borderBottomColor: "rgba(23,24,27,0.8)" } : { left: p.arrow, top: "100%", borderTopColor: "rgba(23,24,27,0.8)" }} />
      )}
    </div>
  );
}

// 397: ukázka úkolu v obláčku, když člověk zatím žádný úkol nemá — ať vidí, na co má klepnout a co se otevře
function TaskDemo() {
  return (
    <span className="block mt-2.5 rounded-[10px] bg-white/[.08] border border-white/10 p-2.5 font-medium" data-testid="guide-demo-task" aria-label="Ukázka úkolu">
      <span className="block text-[11px] font-bold uppercase tracking-[.08em] text-white/50">Ukázka</span>
      <span className="flex items-center gap-2.5 mt-1.5">
        <span className="h-[22px] w-[22px] rounded-full border-2 border-[#e0703f] shrink-0 shadow-[0_0_0_4px_rgba(224,112,63,.25)] motion-safe:animate-pulse" />
        <span className="min-w-0">
          <span className="block text-[13.5px] font-bold text-white truncate">Vyzdít příčku v obýváku</span>
          <span className="block text-[12px] text-white/60">RD Novák · dnes</span>
        </span>
      </span>
      <span className="flex items-center gap-1.5 text-[11.5px] text-white/60 my-1.5 pl-1"><ArrowDown size={13} aria-hidden="true" />klepneš na kolečko</span>
      <span className="flex items-center gap-2 rounded-[8px] bg-white/[.06] p-2">
        <span className="h-9 w-9 rounded-[7px] bg-white/10 grid place-items-center shrink-0"><Camera size={16} aria-hidden="true" /></span>
        <span className="min-w-0 flex-1 text-[12px] text-white/70 leading-tight">Fotka hotové práce<br />a krátká poznámka</span>
        <span className="h-7 px-2.5 rounded-full bg-[#e0703f] text-white text-[11.5px] font-bold inline-flex items-center gap-1 shrink-0"><Check size={12} strokeWidth={3} aria-hidden="true" />Hotovo</span>
      </span>
    </span>
  );
}
const DEMOS = { task: TaskDemo };

export default function OwnerGuide() {
  const { user } = useAuth();
  const role = user?.role;
  const owner = role === "OWNER";
  const on = hasGuide(role);
  const mobile = useIsMobile();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const [focus, setFocus] = useState(null);  // 248: ukázka jednoho kroku (i hotového) bez změny postupu
  const { data: ob } = useQuery({
    queryKey: ["onboarding"],
    queryFn: () => api.get("/onboarding").then((r) => r.data),
    enabled: on,
    staleTime: 0,
    refetchOnWindowFocus: true,
    // 250: i v ukázce kroku z přehledu — jinak se nepozná, že ho majitel právě splnil
    refetchInterval: (q) => (q.state.data?.state === "tour" || focus ? 4000 : false),
  });
  const [picks, setPicks] = useState([]);
  const [size, setSize] = useState(null);   // 266: kolik lidí ve firmě
  const [plan, setPlan] = useState(false);
  const [pass, setPass] = useState(null);
  const [mini, setMini] = useState(false);
  const [cert, setCert] = useState(false);
  const [dlg, setDlg] = useState(false);
  const [busy, setBusy] = useState(false);
  const prevDone = useRef(null);
  const scrollWant = useRef(false);
  const obRef = useRef(null);
  const [hint, setHint] = useState(null);
  const [wi, setWi] = useState(0);   // 248: položka prohlídky (walk) v aktuálním kroku
  const [walkOn, setWalkOn] = useState(false);   // 253: prohlídka s walkWhen běží jen při otevřeném okně (faktura)
  const [hub, setHub] = useState(false);     // 248: přehled průvodce z menu (co umím, co zbývá, Ukázat)
  const [markOk, setMarkOk] = useState(true);   // 248: potvrzení až po prvku manualWhen (AI: nová adresa)
  const [demo, setDemo] = useState(null);   // 397: ukázka v obláčku (krok bez dat — např. žádný úkol)
  const [anchor, setAnchor] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);   // 263: obláček se vyhne otevřenému menu
  const install = useRef(null);
  const [canInstall, setCanInstall] = useState(false);

  obRef.current = ob;
  const orderOf = useCallback((d) => (owner ? guideOrder(d?.picks, !mobile, d?.size) : roleOrder(role, !mobile) || []), [owner, role, mobile]);
  const order = useMemo(() => orderOf(ob), [orderOf, ob]);
  // 248: certifikát až po všech bodech — i těch, které na začátku nevybral (FULL = všechny bolesti)
  const full = useMemo(() => guideFull(role, !mobile, ob?.size), [role, mobile, ob?.size]);
  // 308: znovu do průvodce se všemi body — majitel všechny bolesti, ostatní nemají co vybírat
  const allBody = owner ? { picks: PAINS.map((p) => p.key) } : {};
  const tour = ob?.state === "tour";
  const cur = focus || (tour ? currentStep(order, ob) : null);
  const live = tour || !!focus;   // obláček a zvýraznění běží v průvodci i v ukázce
  const idx = cur ? order.indexOf(cur) : order.length;
  const step = useMemo(() => (cur ? stepOf(cur, role) : null), [cur, role]);   // 308: stálý objekt, jinak by se zvýraznění pořád obnovovalo
  const walk = step?.walk || null;
  const wItem = walk && (!step.walkWhen || walkOn) ? walk[Math.min(wi, walk.length - 1)] : null;
  useEffect(() => { setWi(0); }, [cur]);
  useEffect(() => { if (!walkOn) setWi(0); }, [walkOn]);

  const save = useCallback(async (body) => {
    setBusy(true);
    try { const { data } = await api.put("/onboarding", body); qc.setQueryData(["onboarding"], data); return data; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setBusy(false); }
  }, [qc]);

  const go = useCallback((key, data) => {
    const to = key && stepRoute(key, data || ob);
    if (to && `${loc.pathname}${loc.search}` !== to) nav(to, { state: { from: `${loc.pathname}${loc.search}` } });   // 263: Zpět v zakázce
    scrollWant.current = true;   // po přechodu odjet k zvýrazněnému tlačítku (na telefonu by bylo pod obláčkem)
    setMini(false);
  }, [nav, loc.pathname, loc.search, ob]);

  const backToHub = useCallback(async (msg) => {
    setFocus(null); setPass(null);
    if (msg) toast.success(msg);
    if (loc.pathname !== "/") nav("/");
    const d = qc.getQueryData(["onboarding"]);
    // 265: ohňostroj jen poprvé — s certifikátem (už oslaveno) zůstane otevřený přehled průvodce
    if (d && !d.cert && full.every((k) => d.done?.[k])) { await save({ state: "tour", unskip: true, ...allBody }); return; }
    setHub(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, nav, qc, full, save]);

  const restart = useCallback(async () => {
    try {
      const { data } = await api.post("/onboarding/start");
      qc.setQueryData(["onboarding"], data);
      setPicks([]); setPlan(false); setPass(null); setCert(false); prevDone.current = null;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [qc]);

  // Menu „Průvodce nastavením": po dokončení ukáže certifikát (odtud jde projít znovu), jinak spustí průvodce.
  useEffect(() => {
    if (!on) return undefined;
    const start = async () => {
      const cur0 = obRef.current;
      // 248: z menu přehled (co umím, co zbývá, ukázka kroku) — znovu od nuly jen tlačítkem v přehledu
      // 395: starý účet (průvodce se mu nikdy nenabídl, stav chybí) rovnou do přehledu, bez otázek
      const started = cur0 && (cur0.state !== "new") && (cur0.state == null || cur0.cert || cur0.state === "tour" || cur0.state === "basics"
        || Object.values(cur0.done || {}).some(Boolean));
      if (started) { setFocus(null); setCert(false); setHub(true); qc.invalidateQueries({ queryKey: ["onboarding"] }); return; }   // 250: přehled s čerstvým stavem
      try {
        const { data } = await api.post("/onboarding/start");
        qc.setQueryData(["onboarding"], data);
        setPicks([]); setPlan(false); setPass(null); setCert(false); prevDone.current = null;
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    window.addEventListener("pp-guide-start", start);
    return () => window.removeEventListener("pp-guide-start", start);
  }, [on, qc]);

  // 395: Superadmin nabídl průvodce → po otevření aplikace se jednou sám otevře přehled kroků
  const offer = !!ob?.offer && ob.state !== "new";
  useEffect(() => {
    if (!on || !offer) return;
    setFocus(null); setCert(false); setHub(true);
    save({ offer_seen: true });
  }, [on, offer, save]);

  // Android/Chrome: nabídka „Instalovat aplikaci" pro krok Na plochu.
  useEffect(() => {
    const on = (e) => { e.preventDefault(); install.current = e; setCanInstall(true); };
    window.addEventListener("beforeinstallprompt", on);
    return () => window.removeEventListener("beforeinstallprompt", on);
  }, []);

  // Nově hotový krok → panel „Hotovo".
  useEffect(() => {
    if (!ob || (!tour && !focus)) { prevDone.current = null; return; }
    const prev = prevDone.current;
    prevDone.current = { ...ob.done };
    if (!prev) return;
    // 252: bod splněný z přehledu → zpět na Přehled a znovu tabulka bodů (když něco zbývá), jinak konec s certifikátem
    if (focus) { if (ob.done[focus] && !prev[focus]) backToHub(STEPS[focus].done); return; }
    const fresh = order.find((k) => ob.done[k] && !prev[k] && !(ob.skipped || []).includes(k));
    if (fresh) { setPass(fresh); setMini(false); }
  }, [ob, tour, order, focus, backToHub]);

  // Aplikace už běží z plochy → krok Na plochu je hotový sám.
  useEffect(() => {
    if (cur === "plocha" && standalone() && !busy) save({ mark: "plocha" });
  }, [cur, busy, save]);

  // Zvýraznění tlačítka, na které se má kliknout, a hlídání otevřeného okna (Radix dialog).
  useEffect(() => {
    if (!live) return undefined;
    const groups = (!pass && !wItem && step?.targets) || [];
    const fields = (!pass && !wItem && step?.fields) || [];
    let lit = [];
    let lastId = null;   // nový cíl mimo obrazovku → posunout k němu (např. výběr lidí otevřený pod tlačítkem)
    const sel = (id) => (id.endsWith("*") ? `[data-testid^="${id.slice(0, -1)}"]` : `[data-testid="${id}"]`);
    // 263: id může být i funkce, která prvek najde sama (políčko „zítra“ v šichtovnici)
    const first = (id) => (typeof id === "function" ? id() || null : [...document.querySelectorAll(sel(id))].find(visible) || null);
    const firstAny = (id) => (Array.isArray(id) ? id.map(first).find(Boolean) || null : first(id));   // 253: náhradní cíl
    const probe = (id) => {
      const el = first(id);
      if (!el) return null;
      const inp = el.matches("input,textarea,select") ? el : el.querySelector("input,textarea,select");
      return { el, value: inp ? inp.value : "", text: el.textContent || "" };
    };
    const tick = () => {
      let found = [];
      if (step?.walkWhen) setWalkOn(!!first(step.walkWhen));
      // 263: položka s tDlg — po kliknutí se otevře formulář; obláček jde do rohu s vlastním textem
      const inDlg = !pass && !!wItem?.tDlg && !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
      setMenuOpen(!!document.querySelector('[role="menu"][data-state="open"]'));
      // 397: položka s `empty` — prvek chybí (žádný úkol) → obláček u prázdného seznamu s vlastním textem a ukázkou
      const alt = wItem?.empty && !firstAny(wItem.id) && firstAny(wItem.empty.id) ? wItem.empty : null;
      const f = inDlg ? null : !pass && wItem ? (alt || (firstAny(wItem.id) ? wItem : null)) : pickField(fields, probe);
      setDemo(!inDlg && f?.demo ? f.demo : null);
      if (f) {
        const el = firstAny(f.id);
        found = [f.wrap && el.parentElement ? el.parentElement : el];
      } else {
        for (const g of groups) {
          const ids = Array.isArray(g) ? g : [g];
          found = ids.flatMap((id) => [...document.querySelectorAll(`[data-testid="${id}"]`)]).filter(visible);
          if (found.length) break;
        }
      }
      setHint(inDlg ? wItem.tDlg : f ? f.t : !pass && wItem ? wItem.t : null);
      // Poloha pole pro černou nápovědu se šipkou (jako v prototypu) — jen když se změní
      const r = f && found[0] ? found[0].getBoundingClientRect() : null;
      const a = r ? { t: Math.round(r.top), b: Math.round(r.bottom), l: Math.round(r.left) } : null;
      setAnchor((o) => (o && a && o.t === a.t && o.b === a.b && o.l === a.l) || (!o && !a) ? o : a);
      lit.filter((n) => !found.includes(n)).forEach((n) => n.classList.remove("pp-tour-spot", "pp-tour-spot--in"));
      found.forEach((n) => { n.classList.add("pp-tour-spot"); n.classList.toggle("pp-tour-spot--in", clippedRing(n)); });
      if (scrollWant.current && found.length) { scrollWant.current = false; found[0].scrollIntoView({ block: "center", behavior: "smooth" }); }
      else if (f && found[0] && f.id !== lastId && lastId !== null) {
        const rr = found[0].getBoundingClientRect();
        if (rr.bottom > window.innerHeight || rr.top < 0) found[0].scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
      lastId = f ? f.id : lastId;
      lit = found;
      setDlg(!!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'));
      setMarkOk(!step?.manualWhen || !!first(step.manualWhen));
    };
    tick();
    const t = setInterval(tick, 500);
    let raf = 0;
    const onMove = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(tick); };   // posun formuláře → nápověda jde s polem
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      clearInterval(t); cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onMove, true); window.removeEventListener("resize", onMove);
      lit.forEach((n) => n.classList.remove("pp-tour-spot", "pp-tour-spot--in"));
    };
  }, [live, step, pass, wItem]);
  // 256: obláček u prvku na stránce → panel se sbalí, ať nezakrývá tlačítka (fotky v deníku); šipkou jde rozbalit
  const pillAtEl = !dlg && !!(wItem && anchor && hint);
  useEffect(() => { setMini(pillAtEl); }, [pillAtEl]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!on || !ob || loc.pathname.startsWith("/superadmin")) return null;
  const where = `v menu u svého jména → ${guideLabel(role)}`;

  // --- 1. Otázka a plán (nový účet nebo spuštění z menu)
  if (ob.state === "new") {
    const first = greet(ob.name || user?.name);
    if (!size && ob.size) setSize(ob.size);   // 266: znovu od začátku → velikost firmy předvybraná
    const toggle = (k) => setPicks((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
    const later = async () => {
      await save({ state: "dismissed" });
      toast(`Průvodce najdeš ${where}.`);
    };
    // 308: ostatní role — žádné otázky, jen co je čeká
    if (!owner) {
      const n = order.length;
      return (
        <Overlay testid="guide-welcome">
          <div className="text-[12px] font-extrabold tracking-[.08em] uppercase pp-ink">Vítej v Proyekty{first ? `, ${first}` : ""}</div>
          <h2 className="text-[26px] sm:text-[30px] font-extrabold tracking-tight leading-tight mt-1.5">Ukážu ti {n} {n >= 2 && n <= 4 ? "věci" : "věcí"}, které budeš dělat nejčastěji</h2>
          <p className="text-[14px] text-muted-foreground mt-1.5">Všechno rovnou ve skutečné aplikaci, asi {totalMin(order)} minut. Kdykoli můžeš krok přeskočit a vrátit se k němu později.</p>
          <ol className="mt-4 grid gap-1.5">
            {order.map((k, i) => (
              <li key={k} className="flex items-center gap-3 min-h-[48px] px-3 rounded-[14px] bg-secondary/60">
                <span className="h-7 w-7 rounded-full bg-card border border-border grid place-items-center text-[12.5px] font-bold tabular-nums shrink-0">{i + 1}</span>
                <span className="min-w-0 flex-1 text-[14.5px] font-semibold truncate">{STEPS[k].t}</span>
                <span className="text-[11.5px] font-bold text-muted-foreground whitespace-nowrap">{fmtMin(STEPS[k].min)}</span>
              </li>
            ))}
          </ol>
          <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-2">
            <button type="button" onClick={later} className="h-11 px-3 text-[14px] font-semibold text-muted-foreground hover:text-foreground sm:mr-auto" data-testid="guide-later">Teď ne</button>
            <Button disabled={busy} className="w-full sm:w-auto h-12 px-6 font-bold" data-testid="guide-start"
              onClick={async () => { const d = await save({ state: "tour" }); if (d) { prevDone.current = { ...d.done }; go(currentStep(order, d), d); } }}>
              Jdeme na to
            </Button>
          </div>
        </Overlay>
      );
    }
    if (!plan) {
      // 393: majitel nemá čas — motto, dvě otázky, a kolik minut to zabere (stejně jako plán na další obrazovce)
      const mins = picks.length && size ? Math.round(totalMin(guideOrder(picks, !mobile, size))) : null;
      const minText = mins ? `${mins} ${mins === 1 ? "minutu" : mins <= 4 ? "minuty" : "minut"}` : "pár minut";
      return (
        <Overlay testid="guide-question">
          <div className="text-[12px] font-extrabold tracking-[.08em] uppercase pp-ink">Vítej v Proyekty{first ? `, ${first}` : ""}</div>
          <h2 className="text-[26px] sm:text-[30px] font-extrabold tracking-tight leading-tight mt-1.5">Méně papíru, více klidu. Začneme tím, co tě pálí nejvíc.</h2>
          <p className="text-[14px] text-muted-foreground mt-1.5" data-testid="guide-question-lead">
            Celou aplikaci se učit nemusíš. Odpověz na dvě otázky a během <b className="text-foreground font-semibold">{minText}</b> ti ukážu jen to, co řeší tvůj největší problém. Pak uvidíš všechno, co se ve firmě děje, do 30 vteřin.
          </p>
          {/* 266: velikost firmy → sám nemá koho zvát, malá firma nepotřebuje šichtovnici a plán na zítřek */}
          <div className="mt-4 text-[15px] font-bold">Kolik vás ve firmě je?</div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Kolik vás ve firmě je">
            {SIZES.map((o) => {
              const on = size === o.key;
              return (
                <button key={o.key} type="button" role="radio" aria-checked={on} onClick={() => setSize(o.key)} data-testid={`guide-size-${o.key}`}
                  className={`h-12 px-3 rounded-[14px] border-2 text-[14.5px] font-bold whitespace-nowrap transition-colors ${on ? "border-[hsl(var(--accent-orange))] bg-[hsl(var(--accent-orange)/0.08)]" : "border-border hover:border-[hsl(var(--accent-orange)/0.5)]"}`}>
                  {o.t}
                </button>
              );
            })}
          </div>
          <div className="mt-5 text-[15px] font-bold">Co tě teď nejvíc pálí?</div>
          <div className="text-[13px] text-muted-foreground mt-0.5">Vyber jednu nebo víc věcí.</div>
          <div className="mt-2 grid gap-2">
            {PAINS.map((p) => {
              const on = picks.includes(p.key);
              return (
                <button key={p.key} type="button" onClick={() => toggle(p.key)} aria-pressed={on} data-testid={`guide-pick-${p.key}`}
                  className={`w-full text-left min-h-[56px] px-4 py-3 rounded-[16px] border-2 flex items-center gap-3 transition-colors ${on ? "border-[hsl(var(--accent-orange))] bg-[hsl(var(--accent-orange)/0.08)]" : "border-border hover:border-[hsl(var(--accent-orange)/0.5)]"}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-bold">{p.t}</span>
                    <span className="block text-[12.5px] text-muted-foreground mt-0.5">ukážu: {mobile ? p.ph : p.pc}</span>
                  </span>
                  <span className={`h-6 w-6 rounded-full grid place-items-center shrink-0 ${on ? "bg-[hsl(var(--accent-orange))] text-white" : "border-2 border-border"}`}>{on && <Check size={15} strokeWidth={3} />}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-2">
            <button type="button" onClick={later} className="h-11 px-3 text-[14px] font-semibold text-muted-foreground hover:text-foreground sm:mr-auto" data-testid="guide-later">Teď ne</button>
            <Button onClick={() => setPlan(true)} disabled={!picks.length || !size} className="w-full sm:w-auto h-12 px-6 font-bold" data-testid="guide-to-plan">Ukaž mi to{mins ? ` · ${mins} min` : ""}</Button>
          </div>
        </Overlay>
      );
    }
    const planOrder = guideOrder(picks, !mobile, size);
    return (
      <Overlay testid="guide-plan">
        <div className="text-[12px] font-extrabold tracking-[.08em] uppercase pp-ink">Tvůj průvodce</div>
        <h2 className="text-[24px] sm:text-[28px] font-extrabold tracking-tight leading-tight mt-1.5">{planOrder.length} kroků · asi {totalMin(planOrder)} minut</h2>
        <p className="text-[14px] text-muted-foreground mt-1.5">Základ firmy, pak to, co tě pálí. Kdykoli můžeš krok přeskočit a vrátit se k němu později.</p>
        <ol className="mt-4 grid gap-1.5">
          {planOrder.map((k, i) => (
            <li key={k} className="flex items-center gap-3 min-h-[48px] px-3 rounded-[14px] bg-secondary/60">
              <span className="h-7 w-7 rounded-full bg-card border border-border grid place-items-center text-[12.5px] font-bold tabular-nums shrink-0">{i + 1}</span>
              <span className="min-w-0 flex-1 text-[14.5px] font-semibold truncate">{STEPS[k].t}</span>
              <span className="text-[11.5px] font-bold text-muted-foreground whitespace-nowrap">{STEPS[k].optional ? "nepovinné" : isBase(k) ? "základ" : "podle tebe"} · {fmtMin(STEPS[k].min)}</span>
            </li>
          ))}
        </ol>
        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-2">
          <button type="button" onClick={() => setPlan(false)} className="h-11 px-3 text-[14px] font-semibold text-muted-foreground hover:text-foreground sm:mr-auto">Zpět</button>
          <Button disabled={busy} className="w-full sm:w-auto h-12 px-6 font-bold" data-testid="guide-start"
            onClick={async () => { const d = await save({ picks, size, state: "tour" }); if (d) { setPlan(false); prevDone.current = { ...d.done }; go(currentStep(guideOrder(d.picks, !mobile, d.size), d), d); } }}>
            Jdeme na to
          </Button>
        </div>
      </Overlay>
    );
  }

  // --- 2. Certifikát (po dokončení i z menu): krémová karta na ohňostroji, PDF ke stažení
  const pdf = async () => {
    try {
      const res = await api.get("/onboarding/certificate.pdf", { params: { steps: full.join(",") }, responseType: "blob" });
      saveBlob(res.data, `certifikat-proyekty-${ob.cert?.no || ""}.pdf`);
    } catch (e) { const err = await blobErrorToJson(e); toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (cert && ob.cert) {
    const rows = certRows(full, ob);
    const d = new Date(ob.cert.at);
    return (
      <Dark testid="guide-cert">
        <div className="pp-cert pp-popin relative z-[1] w-full max-w-[600px] p-6 sm:px-10 sm:pt-9 sm:pb-8">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2.5"><span className="h-[34px] w-[34px] rounded-[10px] bg-[#0f0f10] text-white grid place-items-center font-extrabold">P</span><b>Proyekty</b></span>
            <span className="text-[12px] font-extrabold tracking-[.1em] text-[#b4532a]">CERTIFIKÁT</span>
          </div>
          <div className="mt-5 text-[14px] text-[#5f5f67]">Tímto potvrzujeme, že</div>
          <div className="font-heading text-[30px] sm:text-[40px] font-extrabold tracking-tight leading-tight mt-1 mb-1.5 break-words">{ob.name}</div>
          <div className="text-[15px] text-[#3a3a40]">má za sebou zaškolení v aplikaci Proyekty · {ob.company_name}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-[18px] gap-y-[7px] mt-4 mb-[18px]">
            {rows.map((r) => (
              <span key={r} className="flex items-center gap-2 text-[13.5px] font-semibold">
                <i className="h-[18px] w-[18px] rounded-full bg-[#1f9d6f] text-white grid place-items-center shrink-0 not-italic"><Check size={11} strokeWidth={3.5} /></i>{r}
              </span>
            ))}
          </div>
          <div className="flex justify-between gap-3 text-[12.5px] text-[#5f5f67] border-t border-[#ecd9cc] pt-3">
            <span>{isNaN(d) ? "" : `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`} · {ROLE_NAMES[role] || "Majitel"}</span>
            <span className="tabular-nums">{ob.cert.no}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5 mt-[18px]">
            <button type="button" onClick={pdf} data-testid="guide-cert-pdf"
              className="h-12 sm:flex-1 rounded-full bg-[#e0703f] text-white font-bold text-[15px] inline-flex items-center justify-center gap-2"><FileDown size={18} />Stáhnout PDF</button>
            <button type="button" onClick={() => setCert(false)} data-testid="guide-cert-close"
              className="h-12 sm:flex-1 rounded-full bg-[#f1ece6] text-[#141416] font-bold text-[15px]">Zavřít</button>
          </div>
          {ob.state === "done" && (
            <button type="button" onClick={restart} className="mt-3 w-full text-[13px] font-semibold text-[#5f5f67] underline underline-offset-2" data-testid="guide-restart">Projít průvodce znovu</button>
          )}
        </div>
      </Dark>
    );
  }

  // --- 2b. Přehled z menu (248): co umím, co zbývá, ukázka libovolného kroku, pokračovat, znovu od nuly
  if (hub) {
    const doneN = full.filter((k) => ob.done[k]).length;
    const left = full.filter((k) => !ob.done[k]);
    const show = (k) => { setHub(false); setFocus(k); setWi(0); go(k); };
    const cont = async () => {
      setHub(false);
      if (tour) { const nx = currentStep(order, ob); if (nx) go(nx); return; }
      const d = await save({ state: "tour", unskip: true, ...allBody });
      if (d) { prevDone.current = { ...d.done }; go(currentStep(orderOf(d), d), d); }
    };
    const again = async () => {
      setHub(false);   // 250: potvrzení nesmí být pod panelem přehledu
      if (!(await ask("Projít průvodce znovu od začátku?\n\nHotové body se budou počítat znovu od teď. Certifikát uvidíš znovu, až projdeš všechny body."))) { setHub(true); return; }
      restart();
    };
    return (
      <SidePanel testid="guide-hub" mobile={mobile} onClose={() => setHub(false)}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[12px] font-extrabold tracking-[.08em] uppercase pp-ink">Průvodce · {doneN} z {full.length}</div>
            <h2 className="text-[21px] font-extrabold tracking-tight leading-tight mt-1">{left.length ? "Co už umíš a co zbývá" : "Všechno umíš"}</h2>
          </div>
          <button type="button" onClick={() => setHub(false)} aria-label="Zavřít" className="h-10 w-10 -mr-1 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary shrink-0" data-testid="guide-hub-close"><X size={19} /></button>
        </div>
        <p className="text-[13.5px] text-muted-foreground mt-1.5">Potřebuješ si něco připomenout? Dej u bodu Ukázat — provedu tě jen tím krokem.</p>
        <div className="h-2 rounded-full bg-secondary mt-4 overflow-hidden"><div className="h-full rounded-full bg-[hsl(var(--accent-orange))]" style={{ width: `${(doneN / full.length) * 100}%` }} /></div>
        <ol className="mt-3 grid gap-1">
          {full.map((k) => {
            const ok = ob.done[k];
            return (
              <li key={k} className="flex items-center gap-2.5 min-h-[44px] text-[14px]" data-testid={`guide-hub-row-${k}`}>
                <span className={`h-5 w-5 rounded-full grid place-items-center shrink-0 ${ok ? "bg-success text-white" : "border-2 border-[hsl(var(--accent-orange))]"}`}>{ok && <Check size={12} strokeWidth={3} />}</span>
                <span className={`min-w-0 flex-1 truncate ${ok ? "" : "font-semibold"}`}>{STEPS[k].t}</span>
                <span className="text-[12px] text-muted-foreground whitespace-nowrap hidden sm:inline">{ok ? "umíš" : fmtMin(STEPS[k].min)}</span>
                <button type="button" onClick={() => show(k)} className="h-9 px-3 rounded-full border border-border text-[13px] font-bold hover:bg-secondary shrink-0" data-testid={`guide-hub-show-${k}`}>Ukázat</button>
              </li>
            );
          })}
        </ol>
        <div className="mt-4 flex flex-col gap-2">
          {left.length > 0 && <Button disabled={busy} className="w-full h-11 font-bold" onClick={cont} data-testid="guide-hub-continue">Pokračovat zbylými body</Button>}
          {ob.cert && !left.length && <Button className="w-full h-11 font-bold" onClick={() => { setHub(false); setCert(true); }} data-testid="guide-hub-cert"><Award size={17} /> Můj certifikát</Button>}
          <button type="button" disabled={busy} onClick={again} className="h-10 text-[13.5px] font-semibold text-muted-foreground hover:text-foreground" data-testid="guide-hub-restart">Projít celé znovu od začátku</button>
        </div>
      </SidePanel>
    );
  }

  if (!live) return null;

  // --- 3. Panel po hotovém kroku
  if (pass && !focus) {
    const seq = order;
    const next = currentStep(order, ob);
    const doneCount = seq.filter((k) => ob.done[k] || (ob.skipped || []).includes(k)).length;
    return (
      <Overlay testid="guide-pass">
        <div className="flex items-center gap-4">
          <span className="h-14 w-14 rounded-full bg-success text-white grid place-items-center shrink-0 motion-safe:animate-in motion-safe:zoom-in-50"><Check size={30} strokeWidth={3} /></span>
          <div className="min-w-0">
            <div className="text-[12px] font-extrabold tracking-[.08em] uppercase pp-ink">Krok {seq.indexOf(pass) + 1} z {seq.length}</div>
            <h2 className="text-[22px] sm:text-[26px] font-extrabold tracking-tight leading-tight">{STEPS[pass].done}</h2>
          </div>
        </div>
        <div className="h-2 rounded-full bg-secondary mt-5 overflow-hidden"><div className="h-full rounded-full bg-[hsl(var(--accent-orange))] transition-[width] duration-500" style={{ width: `${(doneCount / seq.length) * 100}%` }} /></div>
        <ol className="mt-4 grid gap-1">
          {seq.map((k) => {
            const ok = ob.done[k], sk = (ob.skipped || []).includes(k) && !ok;
            return (
              <li key={k} className={`flex items-center gap-2.5 text-[14px] ${k === next ? "font-bold" : ok || sk ? "text-muted-foreground" : ""}`}>
                <span className={`h-5 w-5 rounded-full grid place-items-center shrink-0 ${ok ? "bg-success text-white" : k === next ? "border-2 border-[hsl(var(--accent-orange))]" : "border-2 border-border"}`}>{ok && <Check size={12} strokeWidth={3} />}</span>
                <span className={`min-w-0 flex-1 truncate ${ok ? "line-through decoration-1" : ""}`}>{STEPS[k].t}</span>
                <span className="text-[12px] text-muted-foreground whitespace-nowrap">{ok ? "hotovo" : sk ? "přeskočeno" : fmtMin(STEPS[k].min)}</span>
              </li>
            );
          })}
        </ol>
        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-2">
          <button type="button" onClick={() => { setPass(null); setMini(true); }} className="h-11 px-3 text-[14px] font-semibold text-muted-foreground hover:text-foreground sm:mr-auto">Později</button>
          <Button onClick={() => { setPass(null); go(next); }} className="w-full sm:w-auto h-12 px-6 font-bold" data-testid="guide-continue">
            {next ? `Pokračovat: ${STEPS[next].t}` : "Dokončit"}
          </Button>
        </div>
      </Overlay>
    );
  }

  // --- 4. Konec podle prototypu: tma, ohňostroj, odznak, „Proyekty umíš"
  if (!cur && !full.every((k) => ob.done[k])) {
    // 248: vybrané kroky hotové, ale ne všechny → bez ohňostroje a certifikátu, seznam toho, co zbývá
    const left = full.filter((k) => !ob.done[k]);
    const more = async () => { const d = await save({ state: "tour", unskip: true, ...allBody }); if (d) { prevDone.current = { ...d.done }; go(currentStep(full, d), d); } };
    const later = async () => { await save({ state: "basics" }); toast(`Zbylé body najdeš ${where}.`); };
    return (
      <Overlay testid="guide-basics">
        <div className="text-[12px] font-extrabold tracking-[.08em] uppercase pp-ink">Základy máš · {full.length - left.length} z {full.length}</div>
        <h2 className="text-[24px] sm:text-[28px] font-extrabold tracking-tight leading-tight mt-1.5">Dobrá práce, {greet(ob.name)}. Základy znáš.</h2>
        <p className="text-[14px] text-muted-foreground mt-1.5">Certifikát dostaneš, až projdeš všechny body — i ty, které jsi {owner ? "na začátku nevybral nebo " : ""}přeskočil. Zbývá {left.length} · asi {totalMin(left)} minut.</p>
        <div className="h-2 rounded-full bg-secondary mt-4 overflow-hidden"><div className="h-full rounded-full bg-[hsl(var(--accent-orange))]" style={{ width: `${((full.length - left.length) / full.length) * 100}%` }} /></div>
        <ol className="mt-4 grid gap-1">
          {full.map((k) => {
            const ok = ob.done[k];
            return (
              <li key={k} className={`flex items-center gap-2.5 text-[14px] ${ok ? "text-muted-foreground" : "font-semibold"}`}>
                <span className={`h-5 w-5 rounded-full grid place-items-center shrink-0 ${ok ? "bg-success text-white" : "border-2 border-[hsl(var(--accent-orange))]"}`}>{ok && <Check size={12} strokeWidth={3} />}</span>
                <span className={`min-w-0 flex-1 truncate ${ok ? "line-through decoration-1" : ""}`}>{STEPS[k].t}</span>
                <span className="text-[12px] text-muted-foreground whitespace-nowrap">{ok ? "hotovo" : fmtMin(STEPS[k].min)}</span>
              </li>
            );
          })}
        </ol>
        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-2">
          <button type="button" disabled={busy} onClick={later} className="h-11 px-3 text-[14px] font-semibold text-muted-foreground hover:text-foreground sm:mr-auto" data-testid="guide-basics-later">Teď ne</button>
          <Button disabled={busy} onClick={more} className="w-full sm:w-auto h-12 px-6 font-bold" data-testid="guide-basics-more">Pokračovat zbylými body</Button>
        </div>
      </Overlay>
    );
  }

  if (!cur) {
    const doneN = full.filter((k) => ob.done[k]).length;
    const finish = async (showCert) => { const d = await save({ state: "done" }); if (d && showCert) setCert(true); };
    return (
      <Dark testid="guide-finish" center>
        <div className="relative z-[1] flex flex-col items-center w-full">
          <span className="pp-popin h-[132px] w-[132px] rounded-full bg-[#e0703f] text-white grid place-items-center shadow-[0_0_0_10px_rgba(224,112,63,.18),0_0_0_22px_rgba(224,112,63,.08)]"><Check size={64} strokeWidth={2.6} /></span>
          <span className="mt-[34px] inline-flex items-center h-[30px] px-3 rounded-full bg-[#232328] text-[13px] font-bold">Zaškolen · {doneN} z {full.length}</span>
          <h2 className="font-heading font-extrabold tracking-[-.02em] text-[36px] lg:text-[60px] leading-[1.03] mt-4">Hotovo, {greet(ob.name)}.<br />Proyekty umíš.</h2>
          <p className="text-[#a7a7b0] text-[15.5px] lg:text-[18px] max-w-[480px] leading-normal mt-3.5 mb-7">{owner ? "Proyekty jsou nastavené a víš, kde co najdeš." : "Víš, kde co najdeš a jak to udělat."} Certifikát i průvodce najdeš kdykoli v menu u svého jména.</p>
          <div className="flex flex-col md:flex-row gap-2.5 w-full md:w-auto">
            <button type="button" disabled={busy} onClick={() => finish(false)} data-testid="guide-finish-work"
              className="h-14 px-[30px] rounded-full bg-[#e0703f] text-white font-bold text-[16px] inline-flex items-center justify-center gap-2">Pustit se do práce <ArrowRight size={18} /></button>
            <button type="button" disabled={busy} onClick={() => finish(true)} data-testid="guide-show-cert"
              className="h-14 px-[26px] rounded-full bg-[#232328] text-[#f4f4f1] font-bold text-[15px] inline-flex items-center justify-center gap-2"><Award size={18} />Můj certifikát</button>
          </div>
        </div>
      </Dark>
    );
  }

  // --- 5. Obláček s aktuálním krokem
  const moveWalk = (i) => {
    const it = walk[i];
    // 263: formulář otevřený jen na ukázku (tDlg) se při Další/Zpět sám zavře — nic se nevyplňuje
    if (walk[wi]?.tDlg && document.querySelector('[role="dialog"][data-state="open"]')) {
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    }
    setWi(i);
    if (it?.route && `${loc.pathname}${loc.search}` !== it.route) nav(it.route);
    scrollWant.current = true;
  };
  // Černá nápověda se šipkou u zvýrazněného prvku (jako v prototypu). U prohlídky (walk) je
  // klikací se Zpět / Další — 253 v okně, 256 i na stránce (deník, nastavení). U horního okraje jde pod prvek.
  const pill = (steps) => {
    const pillNav = () => (
      <span className="flex gap-1.5 mt-2.5">
        {wi > 0 && <button type="button" onClick={() => moveWalk(wi - 1)} data-testid="guide-pill-back"
          className="h-8 px-3 rounded-full text-[13px] font-bold text-white/80 hover:text-white">Zpět</button>}
        {wi < walk.length - 1 && <button type="button" onClick={() => moveWalk(wi + 1)} data-testid="guide-pill-next"
          className="h-8 px-4 rounded-full bg-white text-[#17181b] text-[13px] font-bold">Další</button>}
        {/* 256: na stránce poslední položka rovnou potvrdí krok (Hotovo, rozumím / v ukázce Hotovo).
            263: i v otevřeném okně — jinak se krok (duplicitní faktura) nedal dokončit, dokud se okno nezavřelo */}
        {wi >= walk.length - 1 && (focus && ob.done[cur]
          ? <button type="button" onClick={() => backToHub()} data-testid="guide-pill-done" className="h-8 px-4 rounded-full bg-white text-[#17181b] text-[13px] font-bold">Hotovo</button>
          : step.manual && markOk && <button type="button" disabled={busy} onClick={() => save({ mark: cur })} data-testid="guide-pill-done"
            className="h-8 px-4 rounded-full bg-white text-[#17181b] text-[13px] font-bold">{step.manual}</button>)}
      </span>
    );
    if (!hint || (!anchor && !steps)) return null;
    const w = Math.min(steps ? 340 : 320, window.innerWidth - 24);
    const Demo = demo && DEMOS[demo];
    if (steps && !anchor) {
      return (
        <div className={`fixed z-[60] ${dlg ? "left-3 md:left-6" : "right-3 md:right-6"} bottom-[calc(12px+env(safe-area-inset-bottom))] md:bottom-6 rounded-[14px] bg-[#17181b]/80 backdrop-blur-xl backdrop-saturate-150 border border-white/10 shadow-[0_14px_36px_rgba(0,0,0,0.28)] text-white px-3.5 py-3 text-[13.5px] font-semibold leading-snug pointer-events-auto`}
          style={{ maxWidth: w }} data-testid="guide-pill" onPointerDown={(e) => e.stopPropagation()}>
          <GuideText text={hint} counter={`${Math.min(wi, walk.length - 1) + 1}/${walk.length}`} />
          {Demo && <Demo />}
          {pillNav()}
        </div>
      );
    }
    // 262: obláček jako tmavé sklo (průsvitný, rozmazané pozadí); bílý text má i nad světlou stránkou kontrast přes 7 : 1
    // pole mimo obrazovku (formulář je delší) → nápověda u okraje se šipkou, kam posunout
    const menuEl = menuOpen ? document.querySelector('[role="menu"][data-state="open"]') : null;
    return (
      <PillBox anchor={anchor} w={w} menuEl={menuEl} steps={!!steps}>
        {(off) => (
          <>
            <GuideText text={hint} counter={steps ? `${Math.min(wi, walk.length - 1) + 1}/${walk.length}` : null} prefix={off === "down" ? "↓ " : off === "up" ? "↑ " : ""} />
            {Demo && <Demo />}
            {steps && pillNav()}
          </>
        )}
      </PillBox>
    );
  };
  // Otevřené okno (formulář): jen nápověda u pole — klik mimo okno by ho zavřel.
  if (dlg) return pill(wItem && walk);
  const skip = async () => { await save({ skip: cur }); const d = qc.getQueryData(["onboarding"]); if (d) go(currentStep(order, d), d); };
  const close = async () => {
    if (focus) { setFocus(null); return; }
    if (!(await ask(`Ukončit průvodce?\n\nVrátíš se k němu kdykoli ${where}.`))) return;
    await save({ state: "dismissed" });
  };
  const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const want = wItem?.route || stepRoute(cur, ob);
  const here = !!want && `${loc.pathname}${loc.search}` === want;
  const atEl = pillAtEl;   // 256: prohlídka na stránce — nápověda u prvku, ne v panelu
  return (
    <>
    {atEl && pill(walk)}
    <div role="dialog" aria-label={guideLabel(role)} data-testid="guide-bubble"
      className="fixed z-[45] left-3 right-3 md:left-auto md:right-6 md:w-[380px] bottom-[calc(var(--bottom-nav-h,5rem)+10px+env(safe-area-inset-bottom))] md:bottom-6 bg-card border border-border rounded-[20px] shadow-xl p-4 motion-safe:animate-in motion-safe:slide-in-from-bottom-3 motion-safe:fade-in">
      <div className="flex items-center gap-3">
        {focus ? <Ring n={full.indexOf(cur) + 1} of={full.length} /> : <Ring n={idx + 1} of={order.length} />}
        <button type="button" onClick={() => setMini((m) => !m)} className="min-w-0 flex-1 text-left" aria-expanded={!mini} data-testid="guide-toggle">
          <span className="block text-[11px] font-extrabold tracking-[.08em] uppercase pp-ink">{focus ? (ob.done[cur] ? "Ukázka · umíš" : `Krok ${full.indexOf(cur) + 1} z ${full.length}`) : `Krok ${idx + 1} z ${order.length}${step.optional ? " · nepovinné" : ""}`}</span>
          <span className="block text-[16px] font-bold leading-tight line-clamp-2">{step.t}</span>
        </button>
        <button type="button" onClick={() => setMini((m) => !m)} aria-label={mini ? "Rozbalit" : "Sbalit"} className="h-9 w-9 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary shrink-0">
          {mini ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        <button type="button" onClick={close} aria-label="Ukončit průvodce" className="h-9 w-9 -mr-1 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary shrink-0" data-testid="guide-close"><X size={18} /></button>
      </div>
      {!mini && (
        <>
          <div className="text-[13.5px] text-muted-foreground leading-snug mt-2.5"><GuideText muted text={`${step.d}${cur === "denik" && ob.first_project_name ? ` Ukážeme to na zakázce ${ob.first_project_name}.` : ""}`} /></div>
          {hint && !atEl && <div className="mt-2.5 rounded-[12px] bg-[#17181b]/80 backdrop-blur-xl backdrop-saturate-150 border border-white/10 shadow-[0_14px_36px_rgba(0,0,0,0.28)] text-white px-3 py-2 text-[13.5px] font-semibold leading-snug" data-testid="guide-hint">
            <GuideText text={hint} counter={walk ? `${Math.min(wi, walk.length - 1) + 1}/${walk.length}` : null} /></div>}
          {cur === "telefon" && (
            <div className="mt-3 flex items-center gap-3">
              <Qr />
              <p className="text-[13px] leading-snug">V telefonu otevři <b>Chrome</b> nebo <b>Edge</b> a naskenuj kód (skener je v adresním řádku). Nebo napiš <b>app.proyekty.cz</b>.</p>
            </div>
          )}
          {cur === "plocha" && (
            <p className="text-[13px] leading-snug mt-2.5 rounded-[12px] bg-secondary/70 p-3">
              {ios ? <>Otevři Proyekty v <b>Chrome</b> nebo <b>Edge</b>, klepni na <b>Sdílet</b> a vyber <b>Přidat na plochu</b>. Ze Safari na ploše zůstávají lišty prohlížeče.</>
                : <>V Chrome klepni na <b>⋮</b> vpravo nahoře a vyber <b>Přidat na plochu</b> (nebo Instalovat aplikaci).</>}
            </p>
          )}
          <div className="mt-3.5 flex flex-wrap items-center gap-2">
            {cur === "plocha" && canInstall && (
              <Button className="h-11 px-4 font-bold" onClick={async () => { const e = install.current; if (!e) return; e.prompt(); const r = await e.userChoice; if (r?.outcome === "accepted") save({ mark: "plocha" }); }}>Přidat na plochu</Button>
            )}
            {/* 248: „Ukaž kde“ jen jako cesta na stránku kroku, když na ní člověk není */}
            {want && !here && (
              <Button variant="outline" className="h-11 px-4 font-bold" onClick={() => { if (wItem?.route) { nav(wItem.route); scrollWant.current = true; } else go(cur); }} data-testid="guide-open">{placeLabel(want)}</Button>
            )}
            {walk && !atEl && wi > 0 && (
              <Button variant="ghost" className="h-11 px-3 font-bold" onClick={() => moveWalk(wi - 1)} data-testid="guide-walk-back">Zpět</Button>
            )}
            {walk && !atEl && wi < walk.length - 1 && (
              <Button className="h-11 px-5 font-bold" onClick={() => moveWalk(wi + 1)} data-testid="guide-walk-next">Další</Button>
            )}
            {step.manual && markOk && !(focus && ob.done[cur]) && (!walk || (step.walkWhen && !walkOn) || wi >= walk.length - 1) && (
              <Button className="h-11 px-4 font-bold" disabled={busy} onClick={() => save({ mark: cur })} data-testid="guide-mark">{step.manual}</Button>
            )}
            {/* 252: v ukázce zavírá jen křížek; bod, který už umí, končí Hotovo → zpět na přehled průvodce */}
            {focus ? (ob.done[cur] && (!walk || wi >= walk.length - 1) && (
              <Button className="h-11 px-5 font-bold" onClick={() => backToHub()} data-testid="guide-focus-done">Hotovo</Button>
            )) : (
            <button type="button" onClick={skip} disabled={busy} className="h-11 px-3 ml-auto text-[13.5px] font-semibold text-muted-foreground hover:text-foreground" data-testid="guide-skip">
              {step.optional ? "Přeskočit, udělám později" : "Přeskočit krok"}
            </button>
            )}
          </div>
        </>
      )}
    </div>
    </>
  );
}
